import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  setTargetHours,
  updateProfile,
  useCapacityUtilization,
  useCurrentUserProfile,
  type CustomRangeBounds,
  type RangeKey,
} from '@busy/api';
import { useToast } from '@busy/ui';
import { useT } from '../../lib/i18n';
import { useUI } from '../../store/ui';
import { useWeatherByDate, toLocalISODate } from '../../lib/useWeatherByDate';
import {
  useCapacityForecast,
  useSourceMixByDow,
  type ForecastDayPlan,
} from '../../lib/useCapacityForecast';
import { CapacityHeatmap, HeatmapLegend, type HeatmapRow } from './CapacityHeatmap';
import { HeatmapRowPopover } from './HeatmapRowPopover';
import { SourceMixTile } from './SourceMixTile';
import { TodayConditionsCallout } from './TodayConditionsCallout';
import { CollapsibleSection } from './CollapsibleSection';
import type { SourceMixByDow } from '../../lib/useCapacityForecast';
import {
  OutlookExplainerSheet,
  type OutlookExplainerKind,
} from './OutlookExplainerSheet';
import { HelpHint } from '../HelpHint';

/** How many weeks of history the Week At A Glance heatmap exposes for
 *  each dashboard range. Aligns the swipe-back limit with the rest of
 *  the dashboard's date filter so a barber on 7d only sees this week,
 *  30d sees roughly the last month, etc. ytd opens the full year cap. */
function maxWeeksForRange(range: RangeKey): number {
  switch (range) {
    case 'today':
    case '7d':
      return 1;
    case '14d':
      return 2;
    case '30d':
      return 5; // ~30/7
    case '90d':
      return 13; // ~90/7
    case 'custom':
      // Custom ranges can span up to ~52 weeks (366-day cap). Show
      // them all — the heatmap is informational, no harm in extra.
      return 52;
    case 'ytd':
    default:
      return 52;
  }
}

// Capacity-utilization dashboard tile. Two stories side-by-side:
//   1. THIS WEEK: actual hours vs target, plus the missed-revenue figure
//      derived from the barber's own YTD revenue/hour (cents per hour).
//   2. ANNUAL OUTLOOK: projected at current pace + potential at target +
//      the gap between them. All numbers come from useCapacityUtilization
//      which reads YTD visits + the user's target_hours_per_week.
//
// Three render states:
//   - target_hours_per_week === null → "set your target" CTA → /settings
//   - ytd_visit_count === 0          → "log your first timed cut" empty state
//   - normal data state              → full split tile
//
// Caveats baked into the copy:
//   - early_year_projection (under 4 weeks of YTD data) → "firms up after a
//     few weeks" footnote so a Jan barber doesn't take the projection too
//     literally.
//   - this_week_missed_cents is null when fewer than 4 cuts logged this
//     week → we hide the dollar figure rather than show a noisy estimate.
//   - "Based on cuts logged via the timer" footnote so the barber knows the
//     number assumes consistent timer usage.

export function CapacityTile({
  range,
  customBounds,
}: {
  range?: RangeKey;
  customBounds?: CustomRangeBounds | null;
} = {}) {
  const t = useT();
  const navigate = useNavigate();
  const toast = useToast();
  // Pass the dashboard's range down so the earnings outlook math
  // (the new period $/hr + potential numbers) reflects the same
  // window the rest of the dashboard's metrics use.
  const cap = useCapacityUtilization(range, customBounds);

  // Weather lookup — fetched once at the tile level and shared between
  // the Week At A Glance heatmap (historical chips per day row) and the
  // 7-day Capacity Forecast section (forward-looking planning chips).
  // No zip = empty map = both surfaces fall back gracefully.
  const profile = useCurrentUserProfile();
  const userZip = profile.data?.zip ?? null;
  const { byDate: weatherByDate, forecast } = useWeatherByDate(userZip);
  // Source mix — per-DoW walk-in vs appointment breakdown. Shares the
  // underlying useRecentVisits cache with the forecast hook (same window,
  // same cache key), so this is effectively free. Independent of weather
  // / zip — renders even when forecast can't, because tagging discipline
  // is useful to see regardless of location data.
  const { data: sourceMix } = useSourceMixByDow();

  // 7-day forecast — fetched here at the tile level so its hourly
  // cells can be spliced into the current-week heatmap (future rows
  // replace "not yet" stripes with forecasted utilization). The
  // weekly_heatmaps payload from useCapacityUtilization feeds the
  // per-DoW × per-hour baseline so the synthesis uses real history
  // rather than reinvented assumptions.
  const forecastResult = useCapacityForecast(
    forecast,
    cap.data?.weekly_heatmaps ?? null,
  );
  // Build a YYYY-MM-DD → ForecastDayPlan map for O(1) lookup inside
  // WeekAtAGlance's day-splice memo.
  const forecastByDate = useMemo(() => {
    const m: Record<string, ForecastDayPlan> = {};
    for (const day of forecastResult.data?.days ?? []) m[day.date] = day;
    return m;
  }, [forecastResult.data]);

  // Today's weather entry + today's DoW walk-in fraction — fed into
  // the TodayConditionsCallout so the interpretation copy softens for
  // appointment-heavy barbers (a 20% walk-in barber doesn't need a
  // "storm = slow day" warning — their booked clients still show).
  const todayWeather = weatherByDate[toLocalISODate(new Date())] ?? null;
  const todayDow = new Date().getDay();
  const todayWalkinPct = sourceMix?.[todayDow]?.walkinPct;
  const todayWalkinFraction =
    todayWalkinPct != null ? todayWalkinPct / 100 : 1.0;

  // Earnings-outlook explainer — each stat is tappable; opens a sheet
  // that shows the formula + the user's plugged-in numbers + a coaching
  // tip on how to grow that specific number. Replaces the older "rate
  // tile links to Cut Log" behavior (the cut-log link lives on as a
  // secondary button at the bottom of the earning-rate explainer).
  const [explaining, setExplaining] = useState<OutlookExplainerKind | null>(null);

  // Inline hourly-target picker — writes through to the profile on
  // change so the tile re-renders with the new realism dial. Settings
  // → Profile keeps a backup of the same picker for onboarding /
  // discoverability.
  async function handleTargetChange(pct: number) {
    const res = await updateProfile({ hourly_target_pct: pct });
    if (res.error) toast(res.error.message, 'error');
  }

  // Weekly hours dropdown — writes through to the canonical setTargetHours
  // helper so the change also lands in user_target_hours history (not just
  // the users cache). Custom values >50 (or any non-preset) live in
  // Settings; the dropdown's "Custom" option is a no-op pass-through that
  // just surfaces the current stored value.
  async function handleHoursChange(hrs: number, days: number | null) {
    const res = await setTargetHours(hrs, days ?? undefined);
    if (res.error) toast(res.error.message, 'error');
  }
  async function handleDaysChange(days: number, hrs: number | null) {
    // setTargetHours requires hours as the first arg; pass whatever
    // hours the user already has stored (or 40 as the schema default).
    const res = await setTargetHours(hrs ?? 40, days);
    if (res.error) toast(res.error.message, 'error');
  }

  if (cap.loading) {
    return (
      <div className="rounded-2xl border border-line bg-surface p-4 animate-pulse h-44" />
    );
  }
  if (!cap.data) return null;

  const c = cap.data;

  // No target set → invite the barber to set one. Skips the math entirely.
  if (c.target_hours_per_week == null) {
    return (
      <button
        type="button"
        onClick={() => navigate('/settings?section=profile')}
        className="w-full text-left rounded-2xl border border-dashed border-line bg-bg-alt p-4 flex items-center gap-3 hover:border-navy-900/40 dark:hover:border-gold-500/40 transition-colors"
      >
        <div className="w-12 h-12 rounded-xl bg-navy-900/8 dark:bg-gold-500/15 grid place-items-center text-navy-900 dark:text-gold-400 shrink-0">
          <ClockIcon className="w-6 h-6" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] tracking-[0.16em] uppercase font-extrabold text-ink-500">
            {t('dash.capacity.eyebrow')}
          </div>
          <strong className="block text-base leading-tight mt-0.5">
            {t('dash.capacity.setTargetTitle')}
          </strong>
          <p className="text-[12px] text-ink-500 mt-0.5 leading-snug">
            {t('dash.capacity.setTargetSub')}
          </p>
        </div>
        <ChevronIcon className="w-5 h-5 shrink-0 text-ink-500" />
      </button>
    );
  }

  // No timed cuts at all this year → encourage starting the timer.
  if (c.ytd_visit_count === 0) {
    return (
      <div className="rounded-2xl border border-line bg-bg-alt p-4">
        <div className="text-[10px] tracking-[0.16em] uppercase font-extrabold text-ink-500 mb-1">
          {t('dash.capacity.eyebrow')}
        </div>
        <strong className="block text-base leading-tight">
          {t('dash.capacity.empty.title')}
        </strong>
        <p className="text-[12px] text-ink-500 mt-1 leading-snug">
          {t('dash.capacity.empty.sub')}
        </p>
      </div>
    );
  }

  // Progress bar + % utilized math. When the dashboard range chip is
  // "Today", we collapse from a WEEKLY progress bar (this week's cutting
  // hours vs effective weekly target) to a DAILY progress bar (today's
  // cutting hours vs effective daily target). The weekly mode is the
  // default for every other range.
  //
  // Both modes bake in the realism dial (Target %) the same way Period
  // and Annual Potential do — so a barber setting "Hrs 40, Days 5,
  // Target 80%" reads 100% when they hit their per-day cutting goal,
  // not 75% short-of-raw-target.
  //
  // Daily target  = (weekly hours / days) × Target %
  // Weekly target = weekly hours × Target %
  const isTodayRange = range === 'today';
  const rawWeeklyTarget = c.this_week_target_hours ?? c.target_hours_per_week ?? 0;
  const daysPerWeek = c.target_days_per_week ?? 5;
  const effectiveTarget = isTodayRange
    ? (rawWeeklyTarget / Math.max(1, daysPerWeek)) * (c.hourly_target_pct / 100)
    : rawWeeklyTarget * (c.hourly_target_pct / 100);
  // Daily mode reads today's hours from period_actual_hours (the range
  // already filters to today). Weekly mode reads this_week_actual_hours.
  const actualHours = isTodayRange
    ? c.period_actual_hours
    : c.this_week_actual_hours;
  const utilization =
    actualHours > 0 && effectiveTarget > 0
      ? Math.min(100, Math.round((actualHours / effectiveTarget) * 100))
      : 0;
  const progressGapHours = Math.max(0, effectiveTarget - actualHours);

  // Today-mode missed dollars — gap hours × the period's earning rate.
  // Drives the "X.Xh under target · $Y in capacity" line in the Week
  // At A Glance summary when the dashboard range is 'today'. Null when
  // there's no earning rate yet (under-sample period) so the summary
  // falls back to hours-only just like the weekly path already does.
  const todayMissedCents =
    isTodayRange && progressGapHours > 0 && (c.period_dollar_per_hour_cents ?? 0) > 0
      ? Math.round(progressGapHours * (c.period_dollar_per_hour_cents ?? 0))
      : null;

  return (
    <div className="rounded-2xl border border-line bg-surface p-4 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="text-[10px] tracking-[0.16em] uppercase font-extrabold text-ink-500">
              {t('dash.capacity.eyebrow')}
            </div>
            {/* Hourly target % — leads the chip row because it's the
                "what does fully utilized mean for me" dial and the
                Hrs/Days chips read more naturally to its right on
                mobile (the eye flows: realism dial → target hours →
                target days). Updates hourly_target_pct on change which
                cascades through every number on this tile (heatmap
                color buckets, period potential, year-end potential). */}
            <label className="inline-flex items-center gap-1 text-[10px] tracking-[0.1em] uppercase font-bold text-ink-500 bg-bg-alt border border-line rounded-full pl-2 pr-1 py-0.5 hover:border-navy-900/30 dark:hover:border-gold-500/30 transition-colors cursor-pointer">
              <span>{t('dash.capacity.targetChipLabel')}</span>
              <select
                value={c.hourly_target_pct}
                onChange={(e) => handleTargetChange(parseInt(e.target.value, 10))}
                aria-label={t('settings.profile.hourlyTarget')}
                className="appearance-none bg-transparent border-0 text-[10px] font-extrabold text-navy-900 dark:text-gold-400 tabular-nums focus:outline-none cursor-pointer pr-0"
              >
                {[10, 20, 30, 40, 50, 60, 70, 80, 90, 100].map((p) => (
                  <option key={p} value={p}>
                    {p}%
                  </option>
                ))}
              </select>
              <ChevronDownIcon className="w-2.5 h-2.5 text-navy-900 dark:text-gold-400 -ml-0.5 mr-0.5 shrink-0" />
            </label>
            {/* Hrs + Days are wrapped together in a single inline-flex
                so they always travel as a pair when the parent row
                wraps. On a narrow phone the chip row might break as
                [Target%] / [Hrs Days] across two lines, but Hrs and
                Days will never split across the break — they're the
                same conceptual pair (weekly hours and how those hours
                split into days), so visually they belong together. */}
            <div className="inline-flex items-center gap-2">
              {/* Hrs chip — weekly hours target. Presets 20/30/40/50
                  match Settings; if the stored value isn't one of
                  those (custom value from Settings like 32 or 60), a
                  pass-through "Custom (N)" option keeps the chip
                  truthful without overwriting the value on a re-pick. */}
              {(() => {
                const HOUR_PRESETS = [20, 30, 40, 50] as const;
                const storedHrs = c.target_hours_per_week ?? 40;
                const isCustom = !HOUR_PRESETS.includes(storedHrs as 20 | 30 | 40 | 50);
                return (
                  <label className="inline-flex items-center gap-1 text-[10px] tracking-[0.1em] uppercase font-bold text-ink-500 bg-bg-alt border border-line rounded-full pl-2 pr-1 py-0.5 hover:border-navy-900/30 dark:hover:border-gold-500/30 transition-colors cursor-pointer">
                    <span>{t('dash.capacity.hoursChipLabel')}</span>
                    <select
                      value={storedHrs}
                      onChange={(e) => {
                        const next = parseInt(e.target.value, 10);
                        // No-op when the user picks the synthetic "custom"
                        // option (value === storedHrs already).
                        if (next === storedHrs) return;
                        handleHoursChange(next, c.target_days_per_week);
                      }}
                      aria-label={t('settings.profile.targetHours')}
                      className="appearance-none bg-transparent border-0 text-[10px] font-extrabold text-navy-900 dark:text-gold-400 tabular-nums focus:outline-none cursor-pointer pr-0"
                    >
                      {HOUR_PRESETS.map((h) => (
                        <option key={h} value={h}>
                          {h}
                        </option>
                      ))}
                      {isCustom && (
                        <option value={storedHrs}>
                          {t('dash.capacity.hoursCustomOption', { n: storedHrs })}
                        </option>
                      )}
                    </select>
                    <ChevronDownIcon className="w-2.5 h-2.5 text-navy-900 dark:text-gold-400 -ml-0.5 mr-0.5 shrink-0" />
                  </label>
                );
              })()}
              {/* Days chip — 1–7 picker. Pairs with the Hrs chip so the
                  daily-target math (hours ÷ days) stays honest from
                  the dashboard. */}
              <label className="inline-flex items-center gap-1 text-[10px] tracking-[0.1em] uppercase font-bold text-ink-500 bg-bg-alt border border-line rounded-full pl-2 pr-1 py-0.5 hover:border-navy-900/30 dark:hover:border-gold-500/30 transition-colors cursor-pointer">
              <span>{t('dash.capacity.daysChipLabel')}</span>
              <select
                value={c.target_days_per_week ?? 5}
                onChange={(e) =>
                  handleDaysChange(parseInt(e.target.value, 10), c.target_hours_per_week)
                }
                aria-label={t('settings.profile.targetDays')}
                className="appearance-none bg-transparent border-0 text-[10px] font-extrabold text-navy-900 dark:text-gold-400 tabular-nums focus:outline-none cursor-pointer pr-0"
              >
                {[1, 2, 3, 4, 5, 6, 7].map((d) => (
                  <option key={d} value={d}>
                    {d}
                  </option>
                ))}
              </select>
              <ChevronDownIcon className="w-2.5 h-2.5 text-navy-900 dark:text-gold-400 -ml-0.5 mr-0.5 shrink-0" />
            </label>
            </div>
          </div>
          <h3 className="font-display text-xl leading-tight mt-0.5">
            {/* Outer card title switches with range — "Today vs target"
                when range='today', "This week vs target" otherwise.
                Mirrors the same range-aware switch the progress-bar
                label below already does so the whole card reads
                consistently. */}
            {t(isTodayRange ? 'dash.capacity.titleToday' : 'dash.capacity.title')}
          </h3>
          {/* Daily-target line — surfaces the per-day math derived from
              target_hours_per_week ÷ target_days_per_week so the
              numbers stay honest ("8h per day" not vague "40h target"). */}
          {c.daily_target_hours != null && c.daily_target_hours > 0 && (
            <p className="text-[12px] text-ink-500 mt-1 tabular-nums">
              {t('dash.capacity.dailyTarget', {
                hours: c.daily_target_hours.toFixed(1).replace(/\.0$/, ''),
                days: c.target_days_per_week ?? 5,
              })}
            </p>
          )}
        </div>
        {/* % utilized — tap to open the explainer (formula + your
            numbers + how to grow it). Same pattern as the Earnings
            Outlook stats below. */}
        <button
          type="button"
          onClick={() => setExplaining('utilization')}
          className="text-right group -m-1 p-1 rounded-md hover:bg-bg-alt transition-colors"
          aria-label={t('dash.capacity.utilizationAria')}
        >
          <div className="font-display text-2xl tabular-nums leading-none flex items-baseline justify-end gap-1 group-hover:underline">
            <span>{utilization}%</span>
            <InfoDot className="w-2.5 h-2.5 text-ink-500/60 self-center" />
          </div>
          <div className="text-[10px] uppercase tracking-[0.12em] text-ink-500 font-bold mt-1">
            {t('dash.capacity.utilization')}
          </div>
        </button>
      </div>

      {/* Today's conditions callout — fronts the day's weather as a
          first-class capacity input ("storm forecast — expect a slow
          day"). Pulls today's weather entry from the same map the
          heatmap + forecast use, and today's DoW walk-in fraction
          from the source mix so the interpretation softens for
          appointment-heavy barbers. Suppressed when there's no
          weather data (no zip / API miss). */}
      <TodayConditionsCallout
        today={todayWeather}
        walkinFractionToday={todayWalkinFraction}
      />

      {/* Progress bar — actual vs target. Range-aware: when range='today'
          the label reads "today" and tracks today's hours vs daily target;
          otherwise reads "this week" and tracks weekly hours vs weekly
          target. Both numbers and labels swap together. */}
      <div>
        <div className="flex items-baseline justify-between gap-2 mb-1.5">
          <span className="text-[13px] text-ink-700">
            {t(isTodayRange ? 'dash.capacity.today' : 'dash.capacity.thisWeek', {
              actual: formatHours(actualHours),
              // 1-decimal-max display so non-whole targets (e.g.
              // 35h × 70% = 24.5) read cleanly. Whole numbers strip
              // their trailing .0.
              target: effectiveTarget.toFixed(1).replace(/\.0$/, ''),
            })}
          </span>
          {progressGapHours > 0 && (
            <span className="text-[12px] text-ink-500 tabular-nums">
              {t('dash.capacity.gapHours', { hours: formatHours(progressGapHours) })}
            </span>
          )}
        </div>
        <div className="h-2 rounded-full bg-bg-alt overflow-hidden">
          <div
            className="h-full bg-navy-900 dark:bg-gold-500 transition-all"
            style={{ width: `${utilization}%` }}
          />
        </div>
      </div>

      {/* Activity heatmap — 7 days × 16 hours of "cut hair / open slot /
          off the clock". Replaces the old red "missed $X" callout with a
          forward-looking opportunity framing tied to a visible pattern.
          Only renders when we have enough recent history (4+ cuts in the
          last 4 weeks) — otherwise the working-window inference is too
          noisy to be useful. */}
      {(c.weekly_heatmaps?.length ?? 0) > 0 && (
        <WeekAtAGlance
          // Slice to only the most recent N weeks based on the
          // dashboard's range selector. The full array stays in scope
          // (so the slice is cheap) and the visible window changes
          // when the user picks a wider range.
          weeks={c.weekly_heatmaps.slice(-maxWeeksForRange(range ?? 'ytd'))}
          todayLabel={t('dash.capacity.opportunity.today')}
          range={range}
          // Today-mode summary values — override the week's gap +
          // missed in WeekAtAGlance so the "X.Xh under target · $Y"
          // line reflects today's performance, not the whole week's.
          todayGapHours={progressGapHours}
          todayMissedCents={todayMissedCents}
          weatherByDate={weatherByDate}
          forecastByDate={forecastByDate}
          hourlyTargetPct={c.hourly_target_pct}
        />
      )}

      {/* Source mix — "your typical week by walk-in vs appointment".
          Collapsible — headline summary in the toggle, full per-DoW
          bars on tap. Renders independently of weather/forecast
          availability so a barber without a zip still sees their
          tagging pattern. */}
      {sourceMix && (
        <CollapsibleSection
          titleKey="dash.capacity.sourceMix.title"
          summary={sourceMixHeadline(sourceMix)}
        >
          <SourceMixTile mix={sourceMix} />
        </CollapsibleSection>
      )}

      {/* Earnings outlook — collapsed by default. Summary shows the
          headline rate + period gap so a barber can glance at the
          trigger without expanding the whole block. */}
      <CollapsibleSection
        titleKey="dash.capacity.outlook.eyebrow"
        summary={earningsOutlookHeadline({
          ratePerHourCents: c.period_dollar_per_hour_cents ?? null,
          periodGapCents: c.period_gap_cents ?? null,
          rangeToken: t(`range.compact.${range ?? 'ytd'}`),
        })}
      >
        <div className="space-y-3">
        <div className="flex items-baseline justify-between gap-3">
          <div className="text-[10px] tracking-[0.14em] uppercase font-extrabold text-ink-500">
            {t('dash.capacity.outlook.eyebrow')}
          </div>
          {/* $/hr — period revenue ÷ period actual hours. Tap to see
              the formula + the user's plugged-in numbers + a tip on
              how to grow the rate. The old direct-to-Cut-Log path
              survives as a secondary button at the bottom of the
              explainer sheet. */}
          <button
            type="button"
            onClick={() => setExplaining('earning-rate')}
            disabled={c.period_dollar_per_hour_cents == null}
            aria-label={t('dash.capacity.outlook.rateAria')}
            className="text-right group disabled:cursor-default"
          >
            <div className="font-display text-lg tabular-nums text-navy-900 dark:text-gold-500 leading-none flex items-baseline justify-end gap-1 group-enabled:group-hover:underline">
              <span>
                {c.period_dollar_per_hour_cents != null
                  ? `$${Math.round(c.period_dollar_per_hour_cents / 100)}`
                  : '—'}
                <span className="text-[11px] text-ink-500 font-normal ml-0.5">/hr</span>
              </span>
              <InfoDot className="w-2.5 h-2.5 text-ink-500/60 self-center" />
            </div>
            <div className="text-[10px] uppercase tracking-[0.1em] text-ink-500 font-bold mt-0.5">
              {t('dash.capacity.outlook.rateLabel', {
                range: t(`range.compact.${range ?? 'ytd'}`),
              })}
            </div>
          </button>
        </div>

        {/* Period row — what they earned vs what they could have earned
            in the selected range. Gap surfaces only when non-zero. */}
        <div>
          <div className="text-[10px] tracking-[0.12em] uppercase font-bold text-ink-500 mb-1.5">
            {t('dash.capacity.outlook.periodHeader', {
              range: t(`range.compact.${range ?? 'ytd'}`),
            })}
          </div>
          <div className="grid grid-cols-3 gap-2">
            <Stat
              label={t('dash.capacity.outlook.earned')}
              value={formatCurrencyShort(c.period_revenue_cents)}
              onClick={() => setExplaining('period-earned')}
            />
            {c.period_potential_cents != null && (
              <Stat
                label={t('dash.capacity.outlook.potential')}
                value={formatCurrencyShort(c.period_potential_cents)}
                accent="positive"
                onClick={() => setExplaining('period-potential')}
              />
            )}
            {c.period_gap_cents != null && c.period_gap_cents > 0 && (
              <Stat
                label={t('dash.capacity.outlook.gap')}
                value={formatCurrencyShort(c.period_gap_cents)}
                accent="negative"
                onClick={() => setExplaining('period-gap')}
              />
            )}
          </div>
        </div>

        {/* Year-end row — annualized at the same rate + target. */}
        <div>
          <div className="text-[10px] tracking-[0.12em] uppercase font-bold text-ink-500 mb-1.5">
            {t('dash.capacity.outlook.annualHeader')}
          </div>
          <div className="grid grid-cols-3 gap-2">
            <Stat
              label={t('dash.capacity.annual.projected')}
              value={formatCurrencyShort(c.projected_annual_cents)}
              onClick={() => setExplaining('annual-projected')}
            />
            {c.potential_annual_cents != null && (
              <Stat
                label={t('dash.capacity.annual.potential')}
                value={formatCurrencyShort(c.potential_annual_cents)}
                accent="positive"
                onClick={() => setExplaining('annual-potential')}
              />
            )}
            {c.annual_gap_cents != null && c.annual_gap_cents > 0 && (
              <Stat
                label={t('dash.capacity.annual.gap')}
                value={formatCurrencyShort(c.annual_gap_cents)}
                accent="negative"
                onClick={() => setExplaining('annual-gap')}
              />
            )}
          </div>
        </div>

        </div>
      </CollapsibleSection>

      {/* Single footnote covering both data source (cut timer, 6-min
          floor) AND the short-range volatility warning. The earlier
          inline `earlyCaveat` pill that surfaced when rangeDays < 30
          was removed — the same warning now lives in this footer
          regardless of range, so the tile reads cleaner without the
          duplicate. */}
      <p className="text-[11px] text-ink-500 leading-snug">
        {t('dash.capacity.timerCaveat')}
      </p>

      {/* Explainer sheet — opens whenever the user taps any value in the
          Earnings Outlook block. Single source of truth for "what is
          this number, how is it calculated, and how do I move it?" */}
      {explaining && (
        <OutlookExplainerSheet
          kind={explaining}
          cap={c}
          range={range ?? 'ytd'}
          onClose={() => setExplaining(null)}
        />
      )}
    </div>
  );
}

/* ============================================================================
 * Week-at-a-glance — paged heatmap with prev/next chevrons + horizontal swipe.
 * Defaults to the current week (last entry); barber can swipe back up to a
 * year. The opportunity dollar number renders in the header for whatever week
 * is selected — current OR historical, since YTD revenue/hour applies to both.
 * ========================================================================== */

function WeekAtAGlance({
  weeks,
  todayLabel,
  range,
  todayGapHours,
  todayMissedCents,
  weatherByDate,
  forecastByDate,
  hourlyTargetPct,
}: {
  weeks: Array<import('@busy/api').WeekHeatmap>;
  todayLabel: string;
  /** Realism dial — forwarded to the row-tap popover so the actual-day
   *  body can compute "hours worked" the same way the heatmap colors
   *  do (cells are % of target-utilized hour). */
  hourlyTargetPct: number;
  /** Drives row filtering. When `today` is selected, only today's row
   *  renders (a single 16-cell strip) so the heatmap matches what the
   *  rest of the dashboard's range selector implies. All other ranges
   *  keep the full week grid; swipe-back depth is bounded upstream. */
  range?: RangeKey;
  /** Today's gap-to-target hours (effective daily target − today's
   *  actual hours). Used in the summary line when range='today' so
   *  the "X.Xh under target" reflects TODAY, not the whole week. */
  todayGapHours?: number;
  /** Today's gap × earning rate. Null when the period sample is too
   *  small to back a rate (under-sample mode). */
  todayMissedCents?: number | null;
  /** Optional date→weather map (past 90d + next 7d). Forwarded to the
   *  heatmap so each day row can show a high-temp + condition chip
   *  alongside its label. */
  weatherByDate?: Record<string, import('../../lib/weather').ForecastDay>;
  /** Forecast plans keyed by YYYY-MM-DD. Used to splice future days
   *  onto the CURRENT week's heatmap — past days come from the
   *  weekly_heatmaps payload, future days from the forecast model
   *  (with hourly cells synthesized in computeForecast). Historical
   *  weeks (when the user pages back) ignore this since all their
   *  days are in the past. */
  forecastByDate?: Record<string, import('../../lib/useCapacityForecast').ForecastDayPlan>;
}) {
  const t = useT();
  const language = useUI((s) => s.language);
  const dateLocale = language === 'es' ? 'es-US' : 'en-US';
  const isTodayOnly = range === 'today';

  // Default to the most recent week (this week); user pages back from there.
  const [idx, setIdx] = useState(weeks.length - 1);
  // Tapped row state — drives the HeatmapRowPopover. Cleared on close
  // or by paging to a different week.
  const [tappedRow, setTappedRow] = useState<{
    row: HeatmapRow;
    anchor: DOMRect;
  } | null>(null);
  // Close any open popover when the user pages to a different week
  // (the original row's DOMRect is no longer valid).
  useEffect(() => setTappedRow(null), [idx]);
  // When the visible window changes (the dashboard's range selector
  // expands/contracts the slice), snap back to the most recent entry
  // so the user always starts from this week and pages backward.
  useEffect(() => {
    setIdx(weeks.length - 1);
  }, [weeks.length]);
  // Clamp if data shape changes between renders.
  const safeIdx = Math.max(0, Math.min(weeks.length - 1, idx));
  const week = weeks[safeIdx]!;

  const isThisWeek = safeIdx === weeks.length - 1;
  const isLastWeek = safeIdx === weeks.length - 2;
  const canPrev = safeIdx > 0;
  const canNext = safeIdx < weeks.length - 1;

  // Touch swipe — track first-touch X, decide on touchend whether the
  // gesture exceeded the 40px threshold and which direction. Simple
  // enough to inline without bringing in a gesture library.
  const touchStartX = useRef<number | null>(null);
  function onTouchStart(e: React.TouchEvent) {
    touchStartX.current = e.touches[0]?.clientX ?? null;
  }
  function onTouchEnd(e: React.TouchEvent) {
    const start = touchStartX.current;
    touchStartX.current = null;
    if (start == null) return;
    const end = e.changedTouches[0]?.clientX ?? start;
    const dx = end - start;
    if (Math.abs(dx) < 40) return;
    if (dx > 0 && canPrev) setIdx(safeIdx - 1); // swipe right → older week
    if (dx < 0 && canNext) setIdx(safeIdx + 1); // swipe left → newer week
  }

  // Today's day-start ms — used to classify each row in the current
  // week as past/today (actual data) vs future (forecast data), AND
  // to gate the "no cuts logged" hint to only past/today rows so an
  // early-week view doesn't read as empty just because Wed-Sun are
  // still in the future.
  const todayStartMs = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d.getTime();
  }, []);

  // When the range is `today`, filter the week's days down to just
  // today's row so the heatmap matches what the rest of the dashboard
  // is showing. Otherwise (and only when viewing THE CURRENT week),
  // splice forecast rows in for future days — each future row carries
  // the synthesized hourly cells from the forecast model AND a
  // reference to its full ForecastDayPlan for the tap popover.
  // Historical weeks pass through untouched (all their days are
  // already in the past — nothing to forecast).
  const displayedWeek = useMemo<
    import('@busy/api').WeekHeatmap & {
      days: import('./CapacityHeatmap').HeatmapRow[];
    }
  >(() => {
    if (isTodayOnly) {
      return { ...week, days: week.days.filter((d) => d.is_today) };
    }
    if (!isThisWeek || !forecastByDate) return week;
    const spliced: import('./CapacityHeatmap').HeatmapRow[] = week.days.map((d) => {
      // Past + today: use actual data as-is.
      if (d.day_ms <= todayStartMs) return d;
      // Future day: look up the forecast plan by local date string.
      // No plan (gap in forecast coverage) → row stays as-is and the
      // heatmap renders it with whatever data was already there
      // (which for current-week future days is an empty hours[],
      // producing the gray-stripe "not yet" treatment we used to have).
      const plan = forecastByDate[toLocalISODate(d.day_ms)];
      if (!plan) return d;
      return {
        ...d,
        hours: plan.hours,
        is_forecast: true,
        forecast: plan,
      };
    });
    return { ...week, days: spliced };
  }, [
    week,
    isTodayOnly,
    isThisWeek,
    forecastByDate,
    todayStartMs,
  ]);

  const weekLabel = useMemo(() => {
    if (isTodayOnly) return todayLabel;
    if (isThisWeek) return t('dash.capacity.weekly.thisWeek');
    if (isLastWeek) return t('dash.capacity.weekly.lastWeek');
    const start = new Date(week.week_start_ms);
    const end = new Date(week.week_start_ms + 6 * 24 * 60 * 60 * 1000);
    const sameMonth = start.getMonth() === end.getMonth();
    const startStr = start.toLocaleDateString(dateLocale, { month: 'short', day: 'numeric' });
    const endStr = end.toLocaleDateString(dateLocale, sameMonth
      ? { day: 'numeric' }
      : { month: 'short', day: 'numeric' });
    return `${startStr} – ${endStr}`;
  }, [isTodayOnly, todayLabel, isThisWeek, isLastWeek, week.week_start_ms, dateLocale, t]);

  // True when the visible window has any cuts. Drives the "no cuts
  // logged" italic hint below. Future days (after today) don't count
  // as empty — they haven't happened yet, so an early-week heatmap
  // shouldn't read as "no cuts logged this week" just because Wed-Sun
  // are still in the future.
  const hasAnyActivity = useMemo(
    () =>
      displayedWeek.days.some(
        (d) => d.day_ms <= todayStartMs && d.hours.some((pct) => pct > 0),
      ),
    [displayedWeek.days, todayStartMs],
  );

  return (
    <div
      className="rounded-xl border border-line bg-bg-alt p-3 space-y-2.5"
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      {/* Header row: title + week-pager */}
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="text-[10px] tracking-[0.14em] uppercase font-extrabold text-ink-500 inline-flex items-center gap-1.5">
            <span>{t(isTodayOnly ? 'dash.capacity.weekly.todayEyebrow' : 'dash.capacity.weekly.eyebrow')}</span>
            <HelpHint
              titleKey="dash.capacity.weekly.help.title"
              bodyKey="dash.capacity.weekly.help.body"
              ariaKey="dash.capacity.weekly.help.aria"
            />
          </div>
          <div className="text-[13px] font-bold text-ink-900 dark:text-ink-100 mt-0.5">
            {weekLabel}
          </div>
          <p className="text-[12px] text-ink-700 dark:text-ink-300 mt-1 leading-snug">
            {/* gap_hours is "target hours − actual hours" for the
                selected window. When isTodayOnly, swap in today's
                gap + missed values (passed down by the parent) so the
                line reflects today's performance, not the week's.
                Otherwise we read off the weekly heatmap. */}
            {(() => {
              const gap = isTodayOnly ? todayGapHours ?? 0 : week.gap_hours;
              const missed = isTodayOnly
                ? todayMissedCents ?? null
                : week.missed_cents;
              if (gap > 0 && missed != null && missed > 0) {
                return t('dash.capacity.weekly.summary', {
                  hours: formatGapHours(gap),
                  amount: formatCurrency(missed),
                });
              }
              if (gap > 0) {
                return t('dash.capacity.weekly.summaryHoursOnly', {
                  hours: formatGapHours(gap),
                });
              }
              return t('dash.capacity.weekly.atOrAboveTarget');
            })()}
          </p>
        </div>
        <div className="flex items-center gap-1.5 shrink-0 mt-0.5">
          <button
            type="button"
            onClick={() => canPrev && setIdx(safeIdx - 1)}
            disabled={!canPrev}
            aria-label={t('dash.capacity.weekly.prev')}
            className="w-10 h-10 grid place-items-center rounded-xl border border-line bg-surface text-navy-900 dark:text-gold-500 hover:border-navy-900 dark:hover:border-gold-500 active:scale-95 transition-all disabled:opacity-25 disabled:cursor-not-allowed"
          >
            <ChevronLeftIcon className="w-5 h-5" />
          </button>
          <button
            type="button"
            onClick={() => canNext && setIdx(safeIdx + 1)}
            disabled={!canNext}
            aria-label={t('dash.capacity.weekly.next')}
            className="w-10 h-10 grid place-items-center rounded-xl border border-line bg-surface text-navy-900 dark:text-gold-500 hover:border-navy-900 dark:hover:border-gold-500 active:scale-95 transition-all disabled:opacity-25 disabled:cursor-not-allowed"
          >
            <ChevronIcon className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* "X of Y" position indicator — gives the swipe / chevron nav a
          spatial cue so the user knows they're paging through a finite
          history. Hidden when there's only one week (nothing to page). */}
      {weeks.length > 1 && (
        <div className="text-[10px] text-ink-500 tabular-nums">
          {t('dash.capacity.weekly.position', {
            current: safeIdx + 1,
            total: weeks.length,
          })}
          {' · '}
          <span className="text-ink-700">{t('dash.capacity.weekly.swipeHint')}</span>
        </div>
      )}

      <CapacityHeatmap
        data={displayedWeek}
        todayLabel={todayLabel}
        weatherByDate={weatherByDate}
        onRowTap={(row, anchor) => setTappedRow({ row, anchor })}
      />

      {/* Row-detail popover — past rows show actual stats, forecast
          rows show expected band + confidence. Unified component so
          the user gets a consistent interaction across past/future. */}
      {tappedRow && (
        <HeatmapRowPopover
          row={tappedRow.row}
          anchor={tappedRow.anchor}
          weather={weatherByDate?.[toLocalISODate(tappedRow.row.day_ms)] ?? null}
          hourlyTargetPct={hourlyTargetPct}
          onClose={() => setTappedRow(null)}
        />
      )}

      {/* Empty-window hint — when no cuts have been logged in the
          rendered slice. For today-only this reads as "no cuts logged
          today"; for past weeks it reads as the standard empty-week
          line. Suppressed on the live current week (this-week
          emptiness early in the week isn't a bug to flag). */}
      {!hasAnyActivity && (isTodayOnly || !isThisWeek) && (
        <p className="text-[12px] text-ink-500 italic text-center -mt-1">
          {t(isTodayOnly ? 'dash.capacity.weekly.emptyToday' : 'dash.capacity.weekly.emptyWeek')}
        </p>
      )}
      <HeatmapLegend
        lowLabel={t('dash.capacity.opportunity.legend.low')}
        highLabel={t('dash.capacity.opportunity.legend.high')}
      />
    </div>
  );
}

function Stat({
  label,
  value,
  accent,
  onClick,
}: {
  label: string;
  value: string;
  accent?: 'positive' | 'negative';
  /** When provided, renders the stat as a button that opens the
   *  Earnings Outlook explainer for this metric. A small info dot
   *  signals tappability without crowding the value. */
  onClick?: () => void;
}) {
  const valueClass =
    accent === 'positive'
      ? 'text-green-700 dark:text-green-300'
      : accent === 'negative'
      ? 'text-red-600 dark:text-red-400'
      : 'text-ink-900';
  const valueRow = (
    <div className={`font-display text-base tabular-nums leading-tight ${valueClass} flex items-baseline gap-1`}>
      <span>{value}</span>
      {onClick && <InfoDot className="w-2 h-2 text-ink-500/60 self-center" />}
    </div>
  );
  const labelRow = (
    <div className="text-[10px] uppercase tracking-[0.1em] text-ink-500 font-bold mt-1 leading-tight">
      {label}
    </div>
  );
  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        className="text-left w-full -m-1 p-1 rounded-md hover:bg-bg transition-colors"
      >
        {valueRow}
        {labelRow}
      </button>
    );
  }
  return (
    <div>
      {valueRow}
      {labelRow}
    </div>
  );
}

/** Small info-dot icon. Used as a "tap me" cue next to Earnings Outlook
 *  values. Sized small + low-opacity so it reads as a hint, not a
 *  required action. */
function InfoDot({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <circle cx="12" cy="12" r="10" opacity="0.25" />
      <circle cx="12" cy="8" r="1.4" />
      <rect x="11" y="11" width="2" height="6" rx="1" />
    </svg>
  );
}

// Format an hours value for the capacity tile, returning the full
// "{value}{unit}" string so the unit can flip between "h" and "m"
// depending on magnitude. Rules:
//   - exactly 0 → "0h"
//   - tiny but non-zero (under 6 minutes ≈ 0.1h) → render in minutes,
//     e.g. "3m" — preserves the truth that activity exists when the
//     hour-rounding would otherwise show a flat "0h". Matters mostly
//     for testing flows (1-min cuts) and edge cases like a single
//     5-min cut early in the week.
//   - sub-10h → 1 decimal ("0.5h", "7.5h"), trailing ".0" trimmed
//   - 10h+ → rounded integer ("12h")
function formatHours(h: number): string {
  if (h <= 0) return '0h';
  if (h < 0.1) {
    const min = Math.max(1, Math.round(h * 60));
    return `${min}m`;
  }
  if (h < 10) return `${h.toFixed(1).replace(/\.0$/, '')}h`;
  return `${Math.round(h)}h`;
}

/** Gap = target hours − actual hours for a week. Could be fractional
 *  (e.g. 4.5h under target). Round to a single decimal, drop the
 *  trailing ".0" for clean tiny values. Same convention as
 *  formatHours above but without the multi-unit minute fallback —
 *  this metric is always expressed in hours. */
function formatGapHours(h: number): string {
  if (h <= 0) return '0';
  return h.toFixed(1).replace(/\.0$/, '');
}

function formatCurrency(cents: number): string {
  const dollars = Math.round(cents / 100);
  return `$${dollars.toLocaleString()}`;
}

// Compact for stat cards: $58k / $100k / $1.2M.
function formatCurrencyShort(cents: number): string {
  const dollars = cents / 100;
  if (dollars >= 1_000_000) return `$${(dollars / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`;
  if (dollars >= 10_000)    return `$${Math.round(dollars / 1_000)}k`;
  if (dollars >= 1_000)     return `$${(dollars / 1_000).toFixed(1).replace(/\.0$/, '')}k`;
  return `$${Math.round(dollars).toLocaleString()}`;
}

// Summary string for the collapsed Source-Mix section trigger. Picks
// the heaviest walk-in DoW + an overall walk-in % so the trigger
// answers "what's the pattern" without needing to expand. Returns
// undefined when there's no tagging data — the section's empty state
// inside the SourceMixTile body handles the CTA.
function sourceMixHeadline(mix: SourceMixByDow): string | undefined {
  // Need a localized day-of-week label without pulling i18n into a
  // pure helper. Anchor on 2024-01-07 (a Sunday) and walk forward —
  // matches the same trick used inside SourceMixTile.
  const anchor = new Date(2024, 0, 7);
  const dowLabel = (dow: number) => {
    const d = new Date(anchor);
    d.setDate(anchor.getDate() + dow);
    return d.toLocaleDateString('en-US', { weekday: 'short' });
  };
  let bestDow = -1;
  let bestPct = -1;
  let totalWalkin = 0;
  let totalTagged = 0;
  for (let dow = 0; dow < 7; dow++) {
    const slot = mix[dow];
    if (!slot) continue;
    totalWalkin += slot.walkin;
    totalTagged += slot.tagged;
    if (slot.walkinPct != null && slot.walkinPct > bestPct) {
      bestPct = slot.walkinPct;
      bestDow = dow;
    }
  }
  if (totalTagged === 0) return undefined;
  const overall = Math.round((totalWalkin / totalTagged) * 100);
  if (bestDow < 0) return `${overall}% walk-in overall`;
  return `${overall}% walk-in overall · ${dowLabel(bestDow)} heaviest`;
}

// Summary string for the collapsed Earnings-Outlook trigger. Shows
// the period $/hr (the headline number inside the block) + the
// period gap when non-zero. Empty when neither is meaningful.
function earningsOutlookHeadline({
  ratePerHourCents,
  periodGapCents,
  rangeToken,
}: {
  ratePerHourCents: number | null;
  periodGapCents: number | null;
  rangeToken: string;
}): string | undefined {
  const parts: string[] = [];
  if (ratePerHourCents != null) {
    parts.push(`$${Math.round(ratePerHourCents / 100)}/hr · ${rangeToken}`);
  }
  if (periodGapCents != null && periodGapCents > 0) {
    parts.push(`${formatCurrencyShort(periodGapCents)} gap`);
  }
  return parts.length > 0 ? parts.join(' · ') : undefined;
}

function ClockIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  );
}

function ChevronIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <polyline points="9 18 15 12 9 6" />
    </svg>
  );
}

function ChevronLeftIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <polyline points="15 18 9 12 15 6" />
    </svg>
  );
}

// Small down-chevron for the dropdown chips. Native <select> arrows use
// a system color that's nearly invisible against the dark theme; the
// chips set `appearance-none` to hide that arrow and render this SVG
// instead, in the same navy/gold the value text uses.
function ChevronDownIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}
